import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { MenuItem } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { Menu, MenuModule } from "primeng/menu";
import { TooltipModule } from "primeng/tooltip";

export interface BulkActionEvent {
  action: "export" | "activate" | "updateStatus" | "startProcessing";
  selectedCount: number;
}

/**
 * Bulk Actions Toolbar Component
 * Replicates the old QMC bulk actions menu functionality
 */
@Component({
  selector: "app-bulk-actions-toolbar",
  standalone: true,
  imports: [CommonModule, ButtonModule, MenuModule, TooltipModule],
  template: `
    <!-- Bulk Actions Menu Button (matches old QMC) -->
    <div class="bulk-actions-toolbar">
      <p-button
        #menuButton
        icon="pi pi-ellipsis-v"
        [text]="true"
        [rounded]="true"
        size="small"
        [disabled]="loading"
        (onClick)="menu.toggle($event)"
        pTooltip="Bulk Actions"
        tooltipPosition="bottom"
        styleClass="bulk-menu-button"
        aria-label="Bulk Actions Menu"
      />

      <!-- Bulk Actions Menu -->
      <p-menu #menu [model]="menuItems" [popup]="true" appendTo="body" />
    </div>
  `,
  styles: [
    `
      .bulk-actions-toolbar {
        display: flex;
        align-items: center;
      }

      .bulk-menu-button {
        min-width: 40px;
        min-height: 40px;
      }

      :host ::ng-deep .p-menu {
        min-width: 280px;
      }

      :host ::ng-deep .p-menu .p-menuitem-link {
        padding: 0.75rem 1rem;
      }

      :host ::ng-deep .p-menu .p-menuitem-text {
        margin-left: 0.5rem;
      }
    `,
  ],
})
export class BulkActionsToolbarComponent implements OnChanges {
  @Input() selectedCount = 0;
  @Input() loading = false;

  @Output() bulkAction = new EventEmitter<BulkActionEvent>();

  @ViewChild("menu") menu!: Menu;

  menuItems: MenuItem[] = [];

  private lastActionTime = 0;
  private readonly debounceTime = 500; // 500ms debounce

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["selectedCount"] || changes["loading"]) {
      this.updateMenuItems();
    }
  }

  private updateMenuItems(): void {
    this.menuItems = [
      {
        label: "Export",
        icon: "pi pi-download",
        command: () => {
          this.handleMenuAction("export");
        },
        disabled: this.loading,
      },
      {
        separator: true,
      },
      {
        label: `Activate (${this.selectedCount}) Scheduled Orders`,
        icon: "pi pi-play",
        command: () => {
          this.handleMenuAction("activate");
        },
        disabled: this.loading || this.selectedCount === 0,
      },
      {
        label: `Update Status for (${this.selectedCount}) Scheduled Orders`,
        icon: "pi pi-pencil",
        command: () => {
          this.handleMenuAction("updateStatus");
        },
        disabled: this.loading || this.selectedCount === 0,
      },
      {
        label: `Start Processing (${this.selectedCount}) Scheduled Orders`,
        icon: "pi pi-forward",
        command: () => {
          this.handleMenuAction("startProcessing");
        },
        disabled: this.loading || this.selectedCount === 0,
      },
    ];
  }

  private handleMenuAction(action: BulkActionEvent["action"]): void {
    // Hide the menu immediately
    if (this.menu) {
      this.menu.hide();
    }

    // Emit the action
    this.emitAction(action);
  }

  private emitAction(action: BulkActionEvent["action"]): void {
    const now = Date.now();
    if (now - this.lastActionTime < this.debounceTime) {
      console.log("Debouncing duplicate action:", action);
      return;
    }

    this.lastActionTime = now;
    this.bulkAction.emit({
      action,
      selectedCount: this.selectedCount,
    });
  }
}
